import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { getErrorMessage } from '@digilize/shared/utils/helpers';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { AppContext } from '@plys/src/application/app.context';
import { PaulyApiError } from '@plys/src/application/errors/error.interceptor';
import { CartState } from '@plys/src/application/states/cart.state';
import { CheckoutState } from '@plys/src/application/states/checkout.state';
import { ShowState } from '@plys/src/application/states/show.state';
import { SeatsService } from '@plys/src/infrastructure/seats.service';
import { ResponseError } from '@plys/src/models/response-error';
import { Ticket } from '@plys/src/models/ticket';
import { CartPreviewComponent } from '@plys/src/ui/cart-preview/cart-preview.component';
import { CheckoutUIService } from '@plys/src/ui/checkout/checkout-u-i.service';
import { GoBackDirective } from '@plys/src/ui/checkout/go-back/go-back.directive';
import { ConfirmDialogComponent } from '@plys/src/ui/confirm-dialog/confirm-dialog.component';
import { MobileCartFooterComponent } from '@plys/src/ui/mobile-cart-footer/mobile-cart-footer.component';
import { MovieWidgetComponent } from '@plys/src/ui/movie-widget/movie-widget.component';
import {
  SEAT_PERSON_ICON_CLASS,
  SEAT_PERSON_ICON_PATH,
  SEAT_PERSON_ICON_RATIO,
  SEAT_PERSON_ICON_SIZE,
} from '@plys/src/ui/seat-person-icon';
import { SeatsLegendaComponent } from '@plys/src/ui/seats-legenda/seats-legenda.component';
import { ThemeService } from '@plys/src/ui/theme/theme.service';
import { TooltipService } from '@plys/src/ui/tooltip/tooltip.service';
import {
  catchError,
  combineLatest,
  concatMap,
  filter,
  finalize,
  first,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { SeatsState } from '../../application/states/seats.state';
import { AreasService } from '../../infrastructure/areas.service';

export enum SeatClass {
  SELECTED = 'selected',
  TAKEN = 'taken',
  RESERVING = 'reserving',
  SEAT_ERROR = 'error',
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    MatProgressSpinnerModule,
    CartPreviewComponent,
    MobileCartFooterComponent,
    MovieWidgetComponent,
    SeatsLegendaComponent,
    GoBackDirective,
  ],
  templateUrl: './seats-selection-page.component.html',
  styleUrls: ['./seats-selection-page.component.scss'],
  providers: [CheckoutUIService],
})
export class SeatsSelectionPageComponent implements AfterViewChecked, OnDestroy, OnInit {
  readonly SEAT_ID_REGEX = /^S\d+/;
  readonly MAX_SEATS_TOOLTIP_SELECTOR = '#max-seats-tooltip';
  readonly GAP_NOT_ALLOWED_TOOLTIP_SELECTOR = '#gap-not-allowed-tooltip';
  readonly areaSVG$ = this.getAreaSVG();
  readonly maxSeats$ = this.seatsState.max$;
  readonly isFirstStep$ = this.checkoutState.isFirstStep$;
  readonly reserving$ = this.cartState.reserving$;
  loading = true;
  readonly displaySeatsOnly: boolean = true;

  protected destroy$ = new Subject<void>();
  protected selectionInitialized = false;
  @ViewChild('svgAreaWrapper') svgAreaWrapper: ElementRef<HTMLDivElement>;

  protected seatCustomColors$;

  constructor(
    protected appContext: AppContext,
    // TODO: Move to the show state
    protected areasService: AreasService,
    protected sanitizer: DomSanitizer,
    protected themeService: ThemeService,
    protected seatsState: SeatsState,
    protected cartState: CartState,
    protected showState: ShowState,
    protected tooltip: TooltipService,
    protected checkoutState: CheckoutState,
    protected checkout: CheckoutUIService,
    protected dialog: MatDialog,
    protected transloco: TranslocoService,
    protected seatsService: SeatsService
  ) {}

  ngOnInit(): void {
    this.seatsState.init(null);
    this.refreshTakenSeats();
  }

  protected refreshTakenSeats(): void {
    this.showState.show$
      .pipe(
        filter((show) => !!show),
        first(),
        switchMap((show) => this.seatsState.fetchTaken(show.id)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngAfterViewChecked(): void {
    const { disableSeatsValidation } = this.appContext.get();
    if (!this.selectionInitialized && !this.loading) {
      this.centerAreaView();
      this.markSeatsAs(this.seatsState.selected, SeatClass.SELECTED);
      this.seatsState.taken$.pipe(takeUntil(this.destroy$)).subscribe((takenSeats) => {
        this.markSeatsAs(takenSeats, SeatClass.TAKEN);
      });

      if (!disableSeatsValidation) {
        this.seatsState.gap$.pipe(takeUntil(this.destroy$)).subscribe((gap) => {
          if (gap) {
            this.tooltip.showById(this.GAP_NOT_ALLOWED_TOOLTIP_SELECTOR, gap.id, '.svg-container');
          } else {
            this.tooltip.hide(this.GAP_NOT_ALLOWED_TOOLTIP_SELECTOR);
          }
        });
      }

      this.selectionInitialized = true;
    }
    this.seatCustomColors$ = combineLatest([this.seatsState.types$, this.themeService.theme$]).pipe(
      tap(([types, theme]) => {
        const defaultType = types.find((type) => type.isDefault);
        if (!defaultType) return;
        const { calculatedTakenColorDark, calculatedTakenColorLight, colorDark, colorLight } = defaultType;

        const taken = theme === 'dark' ? calculatedTakenColorDark : calculatedTakenColorLight;
        const normal = theme === 'dark' ? colorDark : colorLight;
        const element = this.svgAreaWrapper.nativeElement;
        element.style.setProperty(`--taken-color`, taken);
        element.style.setProperty(`--normal-color`, normal);
      })
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleSeatClick($event: MouseEvent): void {
    const element = $event.target as HTMLElement;
    let target = element;
    const seatIdRegex = this.SEAT_ID_REGEX;

    if (!element || !element.id) {
      target = element.parentElement;
    }
    this.clearSeatErrors();

    if (!target.id || !seatIdRegex.test(target.id)) {
      return;
    }

    this.toggleSeat(target);
  }

  handleContinueClick(): void {
    this.cartState.cartId$
      .pipe(
        first(),
        switchMap((cartId) => this.seatsService.validateCartReservations(cartId)),
        switchMap(() => this.cartState.requireReservation()),
        first(),
        tap((require) => {
          if (require) this.checkoutState.moveReservingStepAsNextStep(this.cartState.getCartIdValue());
          this.checkout.goNext();
        }),
        catchError((httpError: HttpErrorResponse) => {
          const apiError: PaulyApiError = httpError?.error?.error;

          const errorMessage = getErrorMessage(apiError, (key) => this.transloco.translate(key));

          this.dialog.open(ConfirmDialogComponent, {
            data: {
              title: this.transloco.translate('seats_selection.wrong_seats_selection'),
              message: errorMessage,
              confirmText: 'OK',
            },
          });

          return of(null);
        })
      )
      .subscribe();
  }

  get isStepValid(): boolean {
    return !this.seatsState.isEmpty();
  }

  protected toggleSeat(element: HTMLElement): void {
    const seatId = element.id;
    const isTaken = element.classList.contains(SeatClass.TAKEN);

    if (isTaken) {
      return;
    }

    if (this.seatsState.has(seatId)) {
      this.removeSeat(seatId)
        .pipe(
          catchError((error: ResponseError) => {
            this.checkoutState.setError(error);
            return of(null);
          }),
          first()
        )
        .subscribe(() => {
          element.classList.remove(SeatClass.SELECTED);
          this.removePersonIcon(element);
          this.seatsState.removeTaken(seatId);
          this.seatsState.remove(seatId);
          this.seatsState.verifySingleGap();
        });
    } else {
      if (this.seatsState.canAdd(seatId)) {
        element.classList.add(SeatClass.RESERVING);
        this.cartState.currentTicketsInReserveringPhase++;

        this.addSeat(seatId)
          .pipe(
            catchError((error: ResponseError) => {
              this.checkoutState.setError(error);
              this.refreshTakenSeats();
              return of(null);
            }),
            first(),
            finalize(() => {
              this.cartState.currentTicketsInReserveringPhase--;
              element.classList.remove(SeatClass.RESERVING);
            })
          )
          .subscribe((ticket) => {
            if (ticket) {
              this.seatsState.add(seatId);
              element.classList.add(SeatClass.SELECTED);
              this.addPersonIcon(element);
              this.seatsState.verifySingleGap();
            }
          });
      } else {
        this.tooltip.show(this.MAX_SEATS_TOOLTIP_SELECTOR, element, '.svg-container');
      }
    }
  }

  protected addSeat(seatId: string): Observable<Ticket> {
    return this.showState.defaultProduct$.pipe(
      concatMap((defaultProduct) =>
        this.cartState.addTicketWithSeat(this.seatsState.getSeatData(seatId), defaultProduct)
      )
    );
  }

  protected removeSeat(seatId: string): Observable<Ticket> {
    return this.cartState.removeTicketBySeat(seatId);
  }

  private centerAreaView(): void {
    const svgContainer = document.querySelector('.svg-container') as HTMLElement;
    const svgFigure = document.querySelector('.svg-figure') as HTMLElement;

    if (svgContainer && svgFigure) {
      svgContainer.scrollLeft = (svgFigure.offsetWidth - svgContainer.offsetWidth) / 2;
    }
  }

  private clearSeatErrors(): void {
    this.clearSeatsMarkedAs(SeatClass.SEAT_ERROR);
    this.tooltip.hide(this.MAX_SEATS_TOOLTIP_SELECTOR);
  }

  // Scoped to `.svg-container` so the legend (which reuses the same class names) is left untouched.
  private clearSeatsMarkedAs(className: SeatClass): void {
    const svgContainer = document.querySelector('.svg-container');
    if (!svgContainer) {
      return;
    }
    svgContainer.querySelectorAll(`.${className}`).forEach((element) => {
      element.classList.remove(className);
      if (className === SeatClass.TAKEN || className === SeatClass.SELECTED) {
        this.removePersonIcon(element);
      }
    });
  }

  private readonly PERSON_PATH = SEAT_PERSON_ICON_PATH;
  private readonly PERSON_ICON_CLASS = SEAT_PERSON_ICON_CLASS;

  private readonly WHEELCHAIR_SEAT_GROUP_MARKER = 'wheelchair';

  /** Tags that draw something; text and metadata are never seat artwork. */
  private readonly SEAT_ARTWORK_TAGS = [
    'path',
    'rect',
    'circle',
    'ellipse',
    'polygon',
    'polyline',
    'line',
    'image',
    'use',
    'g',
  ];

  private hasSelfIconedAncestor(element: Element): boolean {
    let current: Element | null = element;
    while (current && current.tagName?.toLowerCase() !== 'svg') {
      if (current.id?.toLowerCase().includes(this.WHEELCHAIR_SEAT_GROUP_MARKER)) return true;
      current = current.parentElement;
    }
    return false;
  }

  /**
   * A seat that already carries its own glyph (wheelchair, and anything else a venue bakes into its
   * SVG) is only recoloured — stacking the person icon on top leaves both unreadable. Group ids are
   * arbitrary (PAUL-2687), so the artwork itself is the marker, not the group name. See PAUL-3517.
   */
  private hasOwnArtwork(container: Element, seat: Element): boolean {
    const drawn = Array.from(container.children).filter(
      (child) =>
        this.SEAT_ARTWORK_TAGS.includes(child.tagName.toLowerCase()) &&
        !child.classList.contains(this.PERSON_ICON_CLASS)
    );

    // A container shared by several seats (flat maps) says nothing about this one seat.
    if (drawn.filter((child) => this.SEAT_ID_REGEX.test(child.id)).length > 1) return false;

    // The seat's own shape is the child carrying its id, or — when the id sits on the container
    // itself — the first shape inside it. Whatever is left over is drawn on top of the seat.
    const seatShape = drawn.find((child) => child.id === seat.id) ?? drawn[0];
    return drawn.some((child) => child !== seatShape);
  }

  private addPersonIcon(element: Element): void {
    const tag = element.tagName.toLowerCase();
    const container: Element = tag === 'g' ? element : element.parentElement ?? element;

    if (this.hasSelfIconedAncestor(container) || this.hasOwnArtwork(container, element)) return;

    if (container.querySelector(`[data-seat="${element.id}"].${this.PERSON_ICON_CLASS}`)) return;

    const rectEl: SVGRectElement | null = tag === 'rect' ? (element as SVGRectElement) : element.querySelector('rect');

    let x: number, y: number, w: number, h: number;

    if (rectEl) {
      x = parseFloat(rectEl.getAttribute('x') ?? '0');
      y = parseFloat(rectEl.getAttribute('y') ?? '0');
      w = parseFloat(rectEl.getAttribute('width') ?? '0');
      h = parseFloat(rectEl.getAttribute('height') ?? '0');
    } else {
      try {
        const bbox = (element as SVGGraphicsElement).getBBox();
        x = bbox.x;
        y = bbox.y;
        w = bbox.width;
        h = bbox.height;
      } catch {
        return;
      }
    }

    if (!w || !h) return;

    const iconSize = SEAT_PERSON_ICON_SIZE;
    const scale = (Math.min(w, h) / iconSize) * SEAT_PERSON_ICON_RATIO;
    const tx = x + (w - iconSize * scale) / 2;
    const ty = y + (h - iconSize * scale) / 2;

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', this.PERSON_PATH);
    path.setAttribute('transform', `translate(${tx}, ${ty}) scale(${scale})`);
    path.classList.add(this.PERSON_ICON_CLASS);
    path.setAttribute('data-seat', element.id);
    path.setAttribute('pointer-events', 'none');
    container.appendChild(path);
  }

  private removePersonIcon(element: Element): void {
    const tag = element.tagName.toLowerCase();
    const container: Element = tag === 'g' ? element : element.parentElement ?? element;
    container.querySelectorAll(`[data-seat="${element.id}"].${this.PERSON_ICON_CLASS}`).forEach((el) => el.remove());
  }

  private markSeatsAs(seats: string[] | Set<string>, className: SeatClass): void {
    seats.forEach((seatId: string) => {
      const element = document.querySelector(`#${seatId}`) as SVGRectElement;

      if (element) {
        element.classList.add(className);
        if (className === SeatClass.TAKEN || className === SeatClass.SELECTED) {
          this.addPersonIcon(element);
        }
      }
    });
  }

  private getAreaSVG(): Observable<SafeHtml> {
    return this.showState.show$.pipe(
      filter((show) => show && !show.disableAssignedSeating),
      // Joined, not chained: the catalogue is prefetched at bootstrap, so this normally resolves at
      // once — but a seat cannot be clicked before the data behind it is in, so the map waits.
      mergeMap((show) =>
        forkJoin([this.seatsState.loadCatalogue(show.shopId, show.areaId), this.fetchAreaSVGById(show.areaId)]).pipe(
          map(([, svg]) => svg),
          // the placeholder stands until both halves are in, so the map is never blank underneath it
          finalize(() => (this.loading = false))
        )
      )
    );
  }

  private fetchAreaSVGById(areaId: string): Observable<SafeHtml> {
    const { theme } = this.appContext.get();
    return this.areasService.getAreaSVG(areaId, theme).pipe(
      map((svg) => this.sanitizer.bypassSecurityTrustHtml(svg)),
      tap(() => this.centerAreaView()),
      catchError((err) => {
        this.loading = false;
        return throwError(() => err);
      })
    );
  }
}
